import React from 'react';
import {
  required, FormDataConsumer, List, SaveButton, Datagrid, DateField, TextField,
  Edit, Create, FormTab, TabbedForm, Toolbar, TextInput, EditButton,
  Filter, NumberInput, Labeled
} from 'react-admin';
import { Box } from '@material-ui/core';
import { CsvUploadComponent } from '../components/CsvUploadComponent';
import { IpListDeleteButton } from '../components/IpListDeleteButton';

const { SectionHeader, formatDate } = require('../components/utils.js');

const RESOURCE = 'advertiser_ip_lists';

const IpListTitle = ({ record }) => (
  <span>IP List {record ? `"${record.name}"` : ''}</span>
);

const UpdatedAtField = ({ record }) => {
  if (!record || !record.updated_at) return <span></span>;
  return <span><nobr>{formatDate(record.updated_at)}</nobr></span>;
};
UpdatedAtField.defaultProps = { addLabel: true, label: 'Updated' };

const IpListFilter = (props) => (
  <Filter {...props}>
    <TextInput label="Search" source="search_name" alwaysOn resettable />
    <NumberInput label="List ID" source="search_id" alwaysOn resettable />
  </Filter>
);

export const AdvertiserIpListList = (props) => (
  <List {...props} filters={<IpListFilter />} perPage={25} bulkActionButtons={false}>
    <Datagrid rowClick="edit">
      <TextField source="code" label="ID" />
      <TextField source="name" label="Name" />
      <TextField source="description" label="Description" />
      <UpdatedAtField label="Updated" />
      <EditButton variant="contained" color="primary" />
      <IpListDeleteButton />
    </Datagrid>
  </List>
);

const CustomToolbar = (props) => (
  <Toolbar {...props}>
    <SaveButton {...props} submitOnEnter={false} />
    <DateField source="updated_at" label="Update date" style={{ padding: '10px' }} showTime />
  </Toolbar>
);

// No Ownership section: advertiser_id is stamped server-side from the session
// and organization_id is not the advertiser's to set (both are stripped by
// advertiser_write_policy), so rendering either would be a control that lies.
const SettingsTabFields = ({ isEdit }) => (
  <Box>
    <SectionHeader title="General" />
    {isEdit && (
      <Labeled label="List ID">
        {/* TextField, not NumberField: a code is an identifier, and NumberField
            applies locale grouping, so list 11105 renders as "11,105". Matches
            the ops publisher and site forms. */}
        <TextField source="code" style={{ fontSize: '1rem' }} />
      </Labeled>
    )}
    <br/>
    <TextInput source="name" label="Name" validate={required()} style={{ maxWidth: 256 }} />
    <Box style={{ gridColumn: '1 / -1' }}>
      <TextInput source="description" label="Description" multiline style={{ width: '50%' }} />
    </Box>
  </Box>
);

const UploadTabFields = () => (
  <FormDataConsumer>
    {({ formData }) => (
      <Box style={{ width: '100%' }}>
        <SectionHeader title="CSV Upload" />
        <CsvUploadComponent
          source="file_upload"
          label="Upload CSV"
          resource={RESOURCE}
          helperText={'CSV with one entry per line: a single IP (1.2.3.4) or a range ' +
            '(1.2.3.0,1.2.3.255). IPv4 and IPv6. Up to 5 MB and 200,000 lines. ' +
            'A file with an invalid line is rejected in full, naming the line.'}
          existingKey={formData && formData.spaces_key}
          code={formData && formData.code}
        />
      </Box>
    )}
  </FormDataConsumer>
);

export const AdvertiserIpListCreate = (props) => (
  <Create title="Create IP List" mutationMode="pessimistic" {...props}>
    <TabbedForm toolbar={<CustomToolbar />}>
      <FormTab label="Settings">
        <SettingsTabFields isEdit={false} />
      </FormTab>
      {/* The Spaces key is derived from `code`, so the Upload tab cannot do
          anything useful until the record exists. */}
      <FormDataConsumer>
        {({ formData, ...rest }) =>
          formData && formData.code ? (
            <FormTab label="Upload" {...rest}><UploadTabFields /></FormTab>
          ) : null
        }
      </FormDataConsumer>
    </TabbedForm>
  </Create>
);

export const AdvertiserIpListEdit = (props) => (
  <Edit title={<IpListTitle />} mutationMode="pessimistic" {...props}>
    <TabbedForm toolbar={<CustomToolbar />}>
      <FormTab label="Settings">
        <SettingsTabFields isEdit={true} />
      </FormTab>
      <FormTab label="Upload">
        <UploadTabFields />
      </FormTab>
    </TabbedForm>
  </Edit>
);
